// ============================================================
//  src/pages/bots/tabs/CommandsTab.js
//  Trigger types, draft/publish workflow, test sandbox.
//  Styles: ./CommandsTab.css (scoped under .cmx-*)
// ============================================================
import React, { useEffect, useRef, useState } from 'react';
import {
  listCommands, createCommand, updateCommand, deleteCommand,
  publishCommand, unpublishCommand, testCommand, toggleCommandActive,
  discardCommandDraft, listCommandVersions, restoreCommandVersion, matchTestCommand,
} from '../../../api/client';
import toast from 'react-hot-toast';
import './tabs.css';
import './CommandsTab.css';

const TYPES = ['command', 'wildcard', 'webhook', 'config', 'flow'];
const LANGUAGES = ['json', 'javascript', 'python'];
const TRIGGER_TYPES = ['exact', 'contains', 'regex', 'wildcard'];

const TYPE_META = {
  command:  { icon: 'fa-terminal',          label: 'Command' },
  wildcard: { icon: 'fa-asterisk',          label: 'Catch-all' },
  webhook:  { icon: 'fa-right-left',        label: 'Webhook' },
  config:   { icon: 'fa-gear',              label: 'Config' },
  flow:     { icon: 'fa-diagram-project',   label: 'Flow' },
};

const LANG_META = {
  javascript: { label: 'JavaScript', short: 'JS',  dot: '#f0b400' },
  python:     { label: 'Python',     short: 'PY',  dot: '#3572A5' },
  json:       { label: 'JSON',       short: '{ }', dot: '#00C88C' },
};

const TRIGGER_TYPE_META = {
  exact:    { label: 'Exact',     icon: 'fa-equals',            hint: 'Fires only when the message matches exactly (not case-sensitive).' },
  contains: { label: 'Contains',  icon: 'fa-magnifying-glass',  hint: 'Fires when the trigger text appears anywhere in the message.' },
  regex:    { label: 'Regex',     icon: 'fa-code',              hint: 'e.g. order #(\\d+) — capture groups become {{match.1}}, {{match.2}}…' },
  wildcard: { label: 'Catch-all', icon: 'fa-asterisk',          hint: 'Runs when nothing else matches. Trigger is always *.' },
};

const DEFAULT_CODE = {
  javascript: `// Available globals: ctx, chat, storage, Panel, WebhookLib, options, fetch
// ctx.bot.sendMessage(chat.id, "Hello!");

module.exports = async () => {
  await ctx.bot.sendMessage(chat.id, "Hello from " + ctx.bot.name);
};`,
  python: `# Available globals: ctx, chat, storage, Panel
# ctx.bot.send_message(chat.id, "Hello!")

async def handler():
    await ctx.bot.send_message(chat.id, "Hello!")`,
  json: `[]`,
};

// ── CodeMirror 6 — lazy loader via npm packages ─────────────
//  RUN ONCE in your project root:
//    npm install @codemirror/state @codemirror/view @codemirror/commands \
//      @codemirror/language @codemirror/autocomplete @codemirror/lint \
//      @codemirror/lang-json @codemirror/lang-javascript @codemirror/lang-python \
//      @codemirror/theme-one-dark
//  and keep src/lib/codemirrorBundle.js.
let _cmPromise = null;
function loadCodeMirror() {
  if (_cmPromise) return _cmPromise;
  _cmPromise = import(/* webpackChunkName: "codemirror" */ '../../../lib/codemirrorBundle')
    .then(m => m.default || m)
    .catch(err => { _cmPromise = null; throw err; });
  return _cmPromise;
}

// ── CommandKit action snippets (autocomplete + "Add action" menu) ──
const ACTION_SNIPPETS = {
  send_text: '{"type":"send_text","text":"Hello {{name}}!"}',
  send_buttons: '{"type":"send_buttons","body":"Choose:","header":"","footer":"","buttons":[{"id":"opt1","title":"Option 1"},{"id":"opt2","title":"Option 2"}]}',
  send_list: '{"type":"send_list","body":"Pick from menu:","button":"View Options","sections":[{"title":"Section","rows":[{"id":"item1","title":"Item 1","description":""}]}]}',
  send_flow: '{"type":"send_flow","flow_id":"{{panel.flow_id}}","header":"Get started","body":"Fill in the form.","footer":"","button_label":"Open"}',
  send_template: '{"type":"send_template","name":"template_name","language":"en_US","components":[]}',
  send_reaction: '{"type":"send_reaction","emoji":"👋","message_id":"{{msg_id}}"}',
  send_raw: '{"type":"send_raw","payload":{}}',
  http_post: '{"type":"http_post","url":"https://api.example.com/endpoint","body":{"key":"{{value}}"},"headers":{"Authorization":"Bearer {{panel.token}}"},"save_as":"result"}',
  http_post_form: '{"type":"http_post_form","url":"https://api.example.com/form","body":{"field":"{{value}}"},"save_as":"result"}',
  set_storage: '{"type":"set_storage","key":"step","value":"{{message}}"}',
  conversation_wait: '{"type":"conversation_wait","command":"/mycommand","step":"awaiting_input"}',
  conversation_clear: '{"type":"conversation_clear"}',
  if_else: '{"type":"if_else","left":"{{message.is_media}}","op":"==","right":"1","then":[{"type":"http_post","url":"https://api.example.com/analyze","body":{"image_url":"{{message.media_url}}","mime":"{{message.mime_type}}"},"headers":{"Authorization":"Bearer {{panel.token}}"},"save_as":"result"},{"type":"send_text","text":"Got your {{message.type}} — checking it now…"}],"else":[{"type":"send_text","text":"Matched!"}]}',
  for_each: '{"type":"for_each","items":"{{result.data}}","actions":[{"type":"send_text","text":"Item: {{loop.item}}"}]}',
  route_flow: '{"type":"route_flow","save_as":"flow_data"}',
  log: '{"type":"log","level":"info","message":"Debug: {{message}}"}',
  wait: '{"type":"wait","seconds":1}',
};

// Grouped for the "Add action" picker
const SNIPPET_GROUPS = [
  { label: 'Send', items: ['send_text', 'send_buttons', 'send_list', 'send_flow', 'send_template', 'send_reaction', 'send_raw'] },
  { label: 'Logic', items: ['if_else', 'for_each', 'wait', 'route_flow'] },
  { label: 'Data', items: ['http_post', 'http_post_form', 'set_storage', 'log'] },
  { label: 'Conversation', items: ['conversation_wait', 'conversation_clear'] },
];

function makeJsonActionCompletion() {
  return function jsonActionCompletions(context) {
    const word = context.matchBefore(/"([a-z_]*)$/);
    const typeVal = context.matchBefore(/"type"\s*:\s*"([a-z_]*)$/);
    if (!word && !typeVal) return null;
    if (typeVal) {
      return {
        from: context.pos - (typeVal[0].length - typeVal[0].indexOf('"', typeVal[0].lastIndexOf(':') + 1) - 1),
        options: Object.keys(ACTION_SNIPPETS).map(k => ({ label: k, type: 'keyword', detail: 'action type', boost: 99 })),
      };
    }
    return null;
  };
}

// ── Trace metadata ─────────────────────────────────────────────
const TRACE_META = {
  send_text:          { icon: 'fa-message',             label: 'Text',        color: '#00A676' },
  send_buttons:       { icon: 'fa-square-check',        label: 'Buttons',     color: '#2563EB' },
  send_list:          { icon: 'fa-list',                label: 'List',        color: '#2563EB' },
  send_flow:          { icon: 'fa-diagram-project',     label: 'Flow',        color: '#7C3AED' },
  send_template:      { icon: 'fa-file-lines',          label: 'Template',    color: '#EA580C' },
  send_reaction:      { icon: 'fa-face-smile',          label: 'Reaction',    color: '#D97706' },
  send_raw:           { icon: 'fa-box',                 label: 'Raw',         color: '#64748B' },
  set_storage:        { icon: 'fa-database',            label: 'Set storage', color: '#0D9488' },
  http_post:          { icon: 'fa-globe',               label: 'HTTP POST',   color: '#0284C7' },
  http_post_form:     { icon: 'fa-globe',               label: 'HTTP form',   color: '#0284C7' },
  conversation_wait:  { icon: 'fa-hourglass-half',      label: 'Wait reply',  color: '#DC2626' },
  conversation_clear: { icon: 'fa-broom',               label: 'Clear conv.', color: '#64748B' },
  if_else:            { icon: 'fa-code-branch',         label: 'If / else',   color: '#9333EA' },
  for_each:           { icon: 'fa-repeat',              label: 'For each',    color: '#1D4ED8' },
  route_flow:         { icon: 'fa-route',               label: 'Route flow',  color: '#0F766E' },
  log:                { icon: 'fa-note-sticky',         label: 'Log',         color: '#64748B' },
  wait:               { icon: 'fa-stopwatch',           label: 'Wait',        color: '#94A3B8' },
  unknown:            { icon: 'fa-circle',              label: 'Message',     color: '#64748B' },
};
const getTraceMeta = (type) => TRACE_META[type] || TRACE_META.unknown;

function parseActions(raw) {
  if (!raw) return [];
  try {
    const v = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(v) ? v : [];
  } catch { return null; }
}

/** One-line human summary of a command's JSON actions for the list. */
function summarize(cmd) {
  if (cmd.cLanguage !== 'json') return { count: null, text: `${LANG_META[cmd.cLanguage]?.label ?? cmd.cLanguage} code` };
  const acts = parseActions(cmd.cActions);
  if (acts === null) return { count: null, text: 'Invalid JSON — open to fix', bad: true };
  if (!acts.length) return { count: 0, text: 'No actions yet' };
  const first = acts.find(a => a?.type === 'send_text' || a?.body || a?.text) || acts[0];
  const txt = first?.text ?? first?.body ?? '';
  return {
    count: acts.length,
    types: acts.map(a => a?.type).filter(Boolean),
    text: txt ? `“${String(txt).replace(/\s+/g, ' ').slice(0, 90)}${String(txt).length > 90 ? '…' : ''}”` : getTraceMeta(first?.type).label,
  };
}

function describePayload(entry) {
  const p = entry.payload || {};
  const type = entry.type || 'unknown';
  if (type === 'send_text' || p.type === 'text') return p.text?.body ?? p.text ?? '(empty)';
  if (type === 'send_buttons') {
    const body = p.interactive?.body?.text ?? p.body ?? '';
    const btns = (p.interactive?.action?.buttons ?? []).map(b => b.reply?.title ?? b.title ?? b.id).join(' · ');
    return body + (btns ? `\n[${btns}]` : '');
  }
  if (type === 'send_list') {
    const body = p.interactive?.body?.text ?? p.body ?? '';
    const rows = (p.interactive?.action?.sections ?? []).flatMap(s => s.rows ?? []).map(r => r.title).slice(0, 5);
    return body + (rows.length ? `\n${rows.join(' · ')}${rows.length === 5 ? ' …' : ''}` : '');
  }
  if (type === 'send_template' || p.template) return `Template: ${p.template?.name ?? '(unnamed)'}`;
  if (type === 'send_reaction') return `Emoji: ${p.reaction?.emoji ?? '?'} on msg ${p.reaction?.message_id ?? '?'}`;
  if (type === 'send_flow') return `Flow ID: ${p.interactive?.action?.parameters?.flow_id ?? '?'}`;
  const str = JSON.stringify(p);
  return str.length > 200 ? str.slice(0, 197) + '…' : str;
}

// ── Shared bits ────────────────────────────────────────────────
function Switch({ checked, onChange, disabled, label }) {
  return (
    <label className={`cmx-switch ${disabled ? 'is-disabled' : ''}`} title={checked ? 'Active — click to turn off' : 'Off — click to turn on'}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />
      <span className="cmx-switch-track" aria-hidden="true" />
      {label !== false && <span className="cmx-switch-label">{label ?? (checked ? 'On' : 'Off')}</span>}
    </label>
  );
}

function RowMenu({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <div className="cmx-menu" ref={ref}>
      <button type="button" className="cmx-icon-btn" aria-label="More actions" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <i className="fa-solid fa-ellipsis" />
      </button>
      {open && (
        <div className="cmx-menu-pop" role="menu">
          {items.map(it => (
            <button key={it.label} type="button" role="menuitem" className={it.danger ? 'is-danger' : ''} disabled={it.disabled}
              onClick={() => { setOpen(false); it.onClick(); }}>
              <i className={`fa-solid ${it.icon}`} /> {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function useBodyLock() {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
}

// ── Command card ──────────────────────────────────────────────
function CommandRow({ cmd, onEdit, onDelete, onPreview, onPublish, onUnpublish, onDiscard, onHistory, onToggleActive, publishing, togglingActive }) {
  const meta = TYPE_META[cmd.cType] ?? { icon: 'fa-circle', label: cmd.cType };
  const lang = LANG_META[cmd.cLanguage] ?? { label: cmd.cLanguage, short: '?', dot: '#999' };
  const tt = TRIGGER_TYPE_META[cmd.cTriggerType] ?? TRIGGER_TYPE_META.exact;
  const isLive = cmd.cStatus === 'live';
  const hasChanges = isLive && !!cmd.cHasDraft;
  const isActive = cmd.cIsActive == 1; // eslint-disable-line eqeqeq
  const isCatchAll = cmd.cTriggerType === 'wildcard' || cmd.cType === 'wildcard';
  const sum = summarize(cmd);
  const phraseCount = parsePhrases(cmd.cPhrases).length;

  return (
    <article className={`cmx-row ${hasChanges ? 'is-changes' : isLive ? 'is-live' : 'is-draft'} ${!isActive ? 'is-off' : ''}`}>
      <div className="cmx-row-main" role="button" tabIndex={0} onClick={() => onEdit(cmd)} title="Edit command"
        onKeyDown={(e) => { if (e.key === 'Enter') onEdit(cmd); }}>
        <div className="cmx-trigger-line">
          <span className={`cmx-trigger ${isCatchAll ? 'is-catch' : ''}`}>
            {isCatchAll ? <><i className="fa-solid fa-asterisk" /> Anything else</> : (cmd.cTrigger || '*')}
          </span>
          <span className="cmx-chip" title={tt.hint}><i className={`fa-solid ${tt.icon}`} /> {tt.label}</span>
          {cmd.cType !== 'command' && cmd.cType !== 'wildcard' && <span className="cmx-chip"><i className={`fa-solid ${meta.icon}`} /> {meta.label}</span>}
          {phraseCount > 0 && !isCatchAll && (
            <span className="cmx-chip is-phr" title={parsePhrases(cmd.cPhrases).slice(0, 8).map(p => p.t).join(' · ')}>
              <i className="fa-regular fa-comments" /> +{phraseCount} phrase{phraseCount === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <div className={`cmx-summary ${sum.bad ? 'is-bad' : ''}`}>
          <span className="cmx-lang" style={{ '--dot': lang.dot }}>{lang.short}</span>
          {sum.count != null && <span className="cmx-count">{sum.count} action{sum.count !== 1 ? 's' : ''}</span>}
          <span className="cmx-summary-text">{sum.text}</span>
        </div>
        {sum.types?.length > 1 && (
          <div className="cmx-flow" aria-hidden="true">
            {sum.types.slice(0, 6).map((t, i) => (
              <span key={i} className="cmx-flow-step" style={{ '--c': getTraceMeta(t).color }} title={getTraceMeta(t).label}>
                <i className={`fa-solid ${getTraceMeta(t).icon}`} />
              </span>
            ))}
            {sum.types.length > 6 && <span className="cmx-flow-more">+{sum.types.length - 6}</span>}
          </div>
        )}
        {hasChanges && (
          <div className="cmx-changes-line">
            <i className="fa-solid fa-pen" /> Unpublished changes{cmd.cDraftAt ? ` · saved ${agoShort(cmd.cDraftAt)}` : ''}
            <span>Customers still get v{cmd.cVersion || 1} · test it from a linked phone</span>
          </div>
        )}
      </div>

      <div className="cmx-row-side">
        <div className="cmx-row-state">
          <span className={`cmx-status ${hasChanges ? 'is-changes' : isLive ? 'is-live' : 'is-draft'}`}
            title={hasChanges ? 'Live, with unpublished changes' : isLive ? `Live${cmd.cVersion ? ` · v${cmd.cVersion}` : ''}` : 'Never published'}>
            <span className="cmx-status-dot" />{hasChanges ? 'Live · edited' : isLive ? 'Live' : 'Draft'}
          </span>
          <Switch checked={isActive} disabled={togglingActive} onChange={(v) => onToggleActive(cmd, v)} />
        </div>
        <div className="cmx-row-actions">
          <button type="button" className="cmx-icon-btn" onClick={() => onPreview(cmd)} title="Preview what this sends" aria-label="Preview command">
            <i className="fa-regular fa-eye" />
          </button>
          {hasChanges ? (
            <button type="button" className="cmx-btn primary sm" onClick={() => onPublish(cmd)} disabled={publishing}>
              {publishing ? <span className="cmx-spin" /> : <i className="fa-solid fa-rocket" />} Publish changes
            </button>
          ) : isLive ? (
            <button type="button" className="cmx-btn ghost sm" onClick={() => onUnpublish(cmd)} disabled={publishing}>
              {publishing ? <span className="cmx-spin" /> : <i className="fa-solid fa-arrow-rotate-left" />} Unpublish
            </button>
          ) : (
            <button type="button" className="cmx-btn primary sm" onClick={() => onPublish(cmd)} disabled={publishing}>
              {publishing ? <span className="cmx-spin" /> : <i className="fa-solid fa-rocket" />} Publish
            </button>
          )}
          <RowMenu items={[
            { label: 'Edit', icon: 'fa-pen', onClick: () => onEdit(cmd) },
            { label: 'Preview', icon: 'fa-eye', onClick: () => onPreview(cmd) },
            { label: 'Version history', icon: 'fa-clock-rotate-left', onClick: () => onHistory(cmd) },
            ...(hasChanges ? [
              { label: 'Discard unpublished changes', icon: 'fa-eraser', onClick: () => onDiscard(cmd) },
              { label: 'Unpublish', icon: 'fa-arrow-rotate-left', onClick: () => onUnpublish(cmd) },
            ] : []),
            { label: 'Delete', icon: 'fa-trash-can', danger: true, onClick: () => onDelete(cmd) },
          ]} />
        </div>
      </div>
    </article>
  );
}

// ── CodeMirror 6 editor ──────────────────────────────────────
function CodeEditor({ value, onChange, language, fill = false }) {
  const hostRef = useRef(null);
  const viewRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const [cmReady, setCmReady] = useState(false);
  const [cmError, setCmError] = useState(false);
  const [editorHeight, setEditorHeight] = useState(420);
  const [cursorInfo, setCursorInfo] = useState({ line: 1, col: 1, lines: 1 });

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    if (!hostRef.current) return undefined;
    let cancelled = false;
    let view = null;

    loadCodeMirror().then((cm) => {
      if (cancelled || !hostRef.current) return;
      const {
        EditorState, EditorView, keymap, lineNumbers,
        highlightActiveLineGutter, drawSelection, dropCursor,
        rectangularSelection, highlightActiveLine, highlightSpecialChars,
        defaultKeymap, history, historyKeymap, indentWithTab,
        defaultHighlightStyle, syntaxHighlighting, indentOnInput,
        bracketMatching, foldKeymap,
        autocompletion, completionKeymap, closeBrackets, closeBracketsKeymap,
        lintKeymap, linter,
        json: jsonLang, jsonParseLinter,
        javascript: jsLang,
        python: pyLang,
        oneDark,
      } = cm;

      const langExt = language === 'json'
        ? [jsonLang(), linter(jsonParseLinter())]
        : language === 'python' ? [pyLang()] : [jsLang()];
      const acExt = language === 'json'
        ? [autocompletion({ override: [makeJsonActionCompletion(cm)] })]
        : [autocompletion()];

      const updateListener = EditorView.updateListener.of((update) => {
        if (update.docChanged) onChangeRef.current(update.state.doc.toString());
        const sel = update.state.selection.main;
        const line = update.state.doc.lineAt(sel.head);
        setCursorInfo({ line: line.number, col: sel.head - line.from + 1, lines: update.state.doc.lines });
      });

      const editorTheme = EditorView.theme({
        '&': { height: '100%', fontSize: '13.5px', fontFamily: "ui-monospace,'SF Mono','JetBrains Mono',Menlo,Consolas,monospace", backgroundColor: '#0F1720' },
        '.cm-scroller': { overflow: 'auto', lineHeight: '1.6' },
        '.cm-content': { padding: '10px 0' },
        '.cm-gutters': { backgroundColor: '#0F1720', borderRight: '1px solid #1F2B37' },
      });

      const state = EditorState.create({
        doc: value,
        extensions: [
          lineNumbers(), highlightActiveLineGutter(), highlightSpecialChars(), history(),
          drawSelection(), dropCursor(), rectangularSelection(), highlightActiveLine(),
          indentOnInput(), syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
          bracketMatching(), closeBrackets(), ...langExt, ...acExt,
          keymap.of([...closeBracketsKeymap, ...defaultKeymap, ...historyKeymap, ...foldKeymap, ...completionKeymap, ...lintKeymap, indentWithTab]),
          oneDark, editorTheme, updateListener,
        ],
      });

      view = new EditorView({ state, parent: hostRef.current });
      viewRef.current = view;
      setCmReady(true);
    }).catch((err) => {
      console.error('[CommandKit] CodeMirror load failed:', err);
      if (!cancelled) setCmError(true);
    });

    return () => {
      cancelled = true;
      if (view) { view.destroy(); viewRef.current = null; }
    };
    // Re-create editor when language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Sync external value changes (language switch, "Add action", Format) into CM6
  const lastValueRef = useRef(value);
  useEffect(() => {
    const view = viewRef.current;
    if (!view) { lastValueRef.current = value; return; }
    const current = view.state.doc.toString();
    if (current !== value && value !== lastValueRef.current) {
      view.dispatch({ changes: { from: 0, to: current.length, insert: value } });
    }
    lastValueRef.current = value;
  }, [value]);

  function onResizeMouseDown(e) {
    e.preventDefault();
    const startY = e.clientY, startH = editorHeight;
    const onMove = (mv) => setEditorHeight(Math.max(220, Math.min(900, startH + mv.clientY - startY)));
    const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  const filename = `handler.${language === 'javascript' ? 'js' : language === 'python' ? 'py' : 'json'}`;
  const h = fill ? undefined : editorHeight;

  return (
    <div className={`cmx-editor ${fill ? 'is-fill' : ''}`}>
      <div className="cmx-editor-tab">
        <span className="cmx-editor-dots"><i /><i /><i /></span>
        <span className="cmx-editor-file"><span className="cmx-lang-dot" style={{ '--dot': LANG_META[language]?.dot }} />{filename}</span>
        <span className="cmx-editor-spacer" />
        {!cmReady && !cmError && <span className="cmx-editor-badge"><span className="cmx-spin" /> Loading editor…</span>}
        {cmError && (
          <span className="cmx-editor-badge is-warn" title="Run: npm install @codemirror/state @codemirror/view @codemirror/commands @codemirror/language @codemirror/autocomplete @codemirror/lint @codemirror/lang-json @codemirror/lang-javascript @codemirror/lang-python @codemirror/theme-one-dark">
            <i className="fa-solid fa-triangle-exclamation" /> Plain mode
          </span>
        )}
      </div>

      <div className={`cmx-cm-host${cmReady ? ' is-ready' : ''}`} ref={hostRef} style={{ height: h }} />
      {!cmReady && (
        <textarea
          className="cmx-cm-fallback"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ height: h }}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          wrap="off"
          aria-label="Code editor"
          placeholder={cmError ? '// CodeMirror failed to load. Edit here.' : '// Loading editor…'}
        />
      )}

      {!fill && <div className="cmx-editor-resize" onMouseDown={onResizeMouseDown} title="Drag to resize editor" role="separator" aria-label="Resize editor" />}

      <div className="cmx-editor-status">
        <span>Ln {cursorInfo.line}, Col {cursorInfo.col}</span>
        <span>{cursorInfo.lines} lines</span>
        <span className="hide-sm">UTF-8</span>
        <span className="right hide-sm">Ctrl+Space autocomplete</span>
      </div>
    </div>
  );
}

// ── Test sandbox ──────────────────────────────────────────────
const VISIBLE_ACTION_TYPES = new Set(['send_text', 'send_buttons', 'send_list', 'send_template', 'send_flow', 'send_reaction', 'send_raw']);

function TraceStep({ entry, index, isLast }) {
  const [expanded, setExpanded] = useState(false);
  const meta = getTraceMeta(entry.type);
  const preview = describePayload(entry);
  const hasDetail = entry.payload && Object.keys(entry.payload).length > 0;
  return (
    <div className="cmx-step">
      <div className="cmx-step-spine">
        <span className="cmx-step-num" style={{ '--c': meta.color }}>{index + 1}</span>
        {!isLast && <span className="cmx-step-line" />}
      </div>
      <div className={`cmx-step-card ${expanded ? 'is-open' : ''}`}>
        <button type="button" className="cmx-step-head" onClick={() => hasDetail && setExpanded(e => !e)} disabled={!hasDetail}>
          <span className="cmx-step-ic" style={{ '--c': meta.color }}><i className={`fa-solid ${meta.icon}`} /></span>
          <span className="cmx-step-label" style={{ color: meta.color }}>{meta.label}</span>
          <span className="cmx-step-preview">{preview.split('\n')[0]}</span>
          {hasDetail && <i className="fa-solid fa-chevron-down cmx-step-chev" />}
        </button>
        {preview.includes('\n') && !expanded && <div className="cmx-step-sub">{preview.split('\n').slice(1).join('\n')}</div>}
        {expanded && <pre className="cmx-step-json">{JSON.stringify(entry.payload, null, 2)}</pre>}
      </div>
    </div>
  );
}

function BubbleContent({ entry }) {
  const p = entry.payload || {};
  const type = entry.type || 'unknown';
  if (type === 'send_text' || p.type === 'text') {
    return <div className="cmx-b-text">{p.text?.body ?? p.text ?? ''}</div>;
  }
  if (type === 'send_buttons') {
    const header = p.interactive?.header?.text;
    const footer = p.interactive?.footer?.text;
    return (
      <>
        {header && <div className="cmx-b-head">{header}</div>}
        <div className="cmx-b-text">{p.interactive?.body?.text ?? p.body ?? ''}</div>
        {footer && <div className="cmx-b-foot">{footer}</div>}
        <div className="cmx-b-btns">
          {(p.interactive?.action?.buttons ?? []).map((b, i) => <div key={i} className="cmx-b-btn">{b.reply?.title ?? b.title ?? b.id}</div>)}
        </div>
      </>
    );
  }
  if (type === 'send_list') {
    const sections = p.interactive?.action?.sections ?? [];
    return (
      <>
        <div className="cmx-b-text">{p.interactive?.body?.text ?? p.body ?? ''}</div>
        <div className="cmx-b-btns"><div className="cmx-b-btn"><i className="fa-solid fa-list-ul" /> {p.interactive?.action?.button ?? 'Choose'}</div></div>
        <div className="cmx-b-list">
          {sections.flatMap(s => s.rows ?? []).slice(0, 4).map((r, i) => (
            <div key={i}><b>{r.title}</b>{r.description && <span>{r.description}</span>}</div>
          ))}
        </div>
      </>
    );
  }
  if (type === 'send_template' || p.template) {
    return <div className="cmx-b-text is-muted"><i className="fa-solid fa-file-lines" /> Template: <b>{p.template?.name ?? '(unnamed)'}</b></div>;
  }
  if (type === 'send_flow') {
    const header = p.interactive?.header?.text;
    return (
      <>
        {header && <div className="cmx-b-head">{header}</div>}
        <div className="cmx-b-text">{p.interactive?.body?.text ?? ''}</div>
        <div className="cmx-b-btns"><div className="cmx-b-btn"><i className="fa-solid fa-table-list" /> {p.interactive?.action?.parameters?.flow_cta ?? 'Open'}</div></div>
      </>
    );
  }
  if (type === 'send_reaction') return <span>reacted {p.reaction?.emoji ?? '👍'} to your message</span>;
  const fallbackText = p.text?.body ?? p.body ?? p.text ?? JSON.stringify(p).slice(0, 200);
  return <div className="cmx-b-text is-muted">{fallbackText}</div>;
}

function ChatBubblePreview({ incomingMessage, transcript }) {
  const visible = (transcript || []).filter(e => VISIBLE_ACTION_TYPES.has(e.type));
  const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return (
    <div className="cmx-phone">
      <div className="cmx-phone-top"><span className="cmx-phone-av"><i className="fa-solid fa-robot" /></span><div><b>Your bot</b><span>online</span></div></div>
      <div className="cmx-phone-screen">
        {incomingMessage && (
          <div className="cmx-b-row is-in"><div className="cmx-bubble is-in"><div className="cmx-b-text">{incomingMessage}</div><div className="cmx-b-time">{now}</div></div></div>
        )}
        {visible.length === 0 ? (
          <div className="cmx-phone-note">This command didn’t send anything the user would see. Check <b>Trace</b> to see what it did (saved data, called an API…).</div>
        ) : visible.map((entry, i) => (
          entry.type === 'send_reaction' ? (
            <div key={i} className="cmx-b-row is-system"><BubbleContent entry={entry} /></div>
          ) : (
            <div key={i} className="cmx-b-row is-out">
              <div className="cmx-bubble is-out"><BubbleContent entry={entry} /><div className="cmx-b-time">{now} <i className="fa-solid fa-check-double" /></div></div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

function TestSandbox({ bId, cmd, language }) {
  const [testMsg, setTestMsg] = useState('');
  const [testing, setTesting] = useState(false);
  const [history, setHistory] = useState([]);
  const [activeRun, setActiveRun] = useState(0);
  const [resultView, setResultView] = useState('chat');
  const inputRef = useRef(null);

  if (language !== 'json') {
    return (
      <div className="cmx-sandbox-off">
        <span className="cmx-ic-lg"><i className="fa-solid fa-flask" /></span>
        <b>JSON commands only</b>
        <span>Switch the language to <strong>JSON</strong> and save — then you can simulate messages and see the full execution trace.</span>
      </div>
    );
  }
  if (!cmd?.cId) {
    return (
      <div className="cmx-sandbox-off">
        <span className="cmx-ic-lg"><i className="fa-solid fa-floppy-disk" /></span>
        <b>Save first</b>
        <span>Create this command as a draft, then come back here to test it.</span>
      </div>
    );
  }

  async function runTest(override) {
    if (testing) return;
    setTesting(true);
    const msg = (override ?? testMsg).trim() || cmd.cTrigger || '';
    let run;
    try {
      const res = await testCommand({ bId, cId: cmd.cId, testMessage: msg });
      const data = res.data ?? {};
      run = {
        id: Date.now(), message: msg || '(empty)', success: data.success ?? false, error: data.error ?? null,
        transcript: data.transcript ?? [], duration_ms: data.duration_ms ?? null, matched: data.matched_groups ?? [],
        ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      };
    } catch (e) {
      run = {
        id: Date.now(), message: msg || '(empty)', success: false, error: e?.response?.data?.message ?? 'Network error',
        transcript: [], duration_ms: null, matched: [], ts: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      };
    }
    setHistory(prev => [run, ...prev].slice(0, 10));
    setActiveRun(0);
    setTesting(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  const displayed = history[activeRun];
  const chips = ['Hello', '/start', cmd.cTrigger].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i && v !== '*');

  return (
    <div className="cmx-sandbox">
      <div className="cmx-sim">
        <div className="cmx-sim-input">
          <i className="fa-regular fa-message" />
          <input
            ref={inputRef}
            placeholder={`Type a message… (default: “${cmd.cTrigger || '*'}”)`}
            value={testMsg}
            onChange={e => setTestMsg(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runTest()}
          />
        </div>
        <button type="button" className="cmx-btn primary" onClick={() => runTest()} disabled={testing}>
          {testing ? <><span className="cmx-spin" /> Running…</> : <><i className="fa-solid fa-play" /> Run</>}
        </button>
      </div>

      {!displayed ? (
        <div className="cmx-sandbox-hint">
          <span className="cmx-ic-lg"><i className="fa-solid fa-vial" /></span>
          <b>Message simulator</b>
          <span>Runs your actions through the real engine with a mock sender — nothing is sent to WhatsApp.</span>
          <div className="cmx-sandbox-chips">
            {chips.map(s => <button key={s} type="button" onClick={() => { setTestMsg(s); runTest(s); }}>{s}</button>)}
          </div>
        </div>
      ) : (
        <div className="cmx-result">
          {history.length > 1 && (
            <div className="cmx-runs">
              {history.map((run, i) => (
                <button key={run.id} type="button" className={`${i === activeRun ? 'is-active' : ''} ${run.success ? 'is-ok' : 'is-err'}`} onClick={() => setActiveRun(i)} title={run.message}>
                  <span className="dot" />{run.ts}
                </button>
              ))}
            </div>
          )}

          <div className={`cmx-run-status ${displayed.success ? 'is-ok' : 'is-err'}`}>
            <i className={`fa-solid ${displayed.success ? 'fa-circle-check' : 'fa-circle-xmark'}`} />
            <span className="msg">
              <b>“{displayed.message}”</b>
              {displayed.success
                ? ` → ${displayed.transcript.length} action${displayed.transcript.length !== 1 ? 's' : ''} ran`
                : ` → ${displayed.error}`}
            </span>
            {displayed.duration_ms !== null && <span className="dur">{displayed.duration_ms}ms</span>}
          </div>

          {displayed.matched?.length > 0 && (
            <div className="cmx-captures">
              <span>Regex captures</span>
              {displayed.matched.map((g, i) => <code key={i}><em>{'{{match.' + (i + 1) + '}}'}</em> {g}</code>)}
            </div>
          )}

          {displayed.success && (
            <div className="cmx-seg small" role="tablist">
              <button type="button" role="tab" aria-selected={resultView === 'chat'} className={resultView === 'chat' ? 'is-active' : ''} onClick={() => setResultView('chat')}>
                <i className="fa-brands fa-whatsapp" /> Chat preview
              </button>
              <button type="button" role="tab" aria-selected={resultView === 'trace'} className={resultView === 'trace' ? 'is-active' : ''} onClick={() => setResultView('trace')}>
                <i className="fa-solid fa-list-ol" /> Trace
              </button>
            </div>
          )}

          {displayed.success && resultView === 'chat' && <ChatBubblePreview incomingMessage={displayed.message} transcript={displayed.transcript} />}

          {(!displayed.success || resultView === 'trace') && (
            displayed.transcript.length > 0 ? (
              <div className="cmx-trace">
                {displayed.transcript.map((entry, i) => <TraceStep key={i} entry={entry} index={i} isLast={i === displayed.transcript.length - 1} />)}
              </div>
            ) : displayed.success ? (
              <div className="cmx-sandbox-off small"><i className="fa-solid fa-bolt" /> Ran successfully but sent no outbound messages.</div>
            ) : null
          )}
        </div>
      )}
    </div>
  );
}

// ── Add-action picker (JSON) ──────────────────────────────────
function AddActionMenu({ onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div className="cmx-add" ref={ref}>
      <button type="button" className="cmx-tool-btn" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <i className="fa-solid fa-plus" /> Add action
      </button>
      {open && (
        <div className="cmx-add-pop">
          {SNIPPET_GROUPS.map(g => (
            <div key={g.label} className="cmx-add-group">
              <span>{g.label}</span>
              <div>
                {g.items.map(k => (
                  <button key={k} type="button" onClick={() => { setOpen(false); onPick(k); }}>
                    <i className={`fa-solid ${getTraceMeta(k).icon}`} style={{ color: getTraceMeta(k).color }} /> {k}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Editor (full-screen) ──────────────────────────────────────
// ── Extra phrases ("i need mobile data" → /data) ──────────────
const PHRASE_MODES = {
  exact:    { label: 'Exact',      icon: 'fa-equals',               hint: 'The whole message is this',          eg: '“menu” — but not “menu please”' },
  contains: { label: 'Contains',   icon: 'fa-magnifying-glass',     hint: 'Appears anywhere, as whole words',   eg: '“data” catches “send me data”' },
  similar:  { label: 'Similar',    icon: 'fa-wand-magic-sparkles',  hint: 'Forgives typos and word order',      eg: '“data” catches “dataa”, “dat”' },
  ai:       { label: 'AI meaning', icon: 'fa-brain',                hint: 'Same meaning, different words',      eg: '“i want internet” catches “my data don finish”' },
};
const PHRASE_ORDER = ['exact', 'contains', 'similar', 'ai'];
const FOUND_LABELS = { 'found.size': 'Size', 'found.size_mb': 'MB', 'found.network': 'Network', 'found.phone': 'Phone', 'found.amount': 'Amount', 'found.cable': 'Cable', 'found.number': 'Number' };

function parsePhrases(raw) {
  let d = raw;
  if (typeof raw === 'string') { try { d = JSON.parse(raw); } catch { d = []; } }
  return Array.isArray(d) ? d.filter(p => p && typeof p.t === 'string').map(p => ({ t: p.t, m: PHRASE_MODES[p.m] ? p.m : 'contains' })) : [];
}
const phraseKey = (p) => `${p.m}|${p.t.trim().toLowerCase().replace(/\s+/g, ' ')}`;

function PhrasesPanel({ bId, cmdId, trigger, triggerType, isActive, phrases, onChange }) {
  const [text, setText] = useState('');
  const [mode, setMode] = useState('contains');
  const [bulk, setBulk] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [tryText, setTryText] = useState('');
  const [trying, setTrying] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const name = trigger || 'this command';
  const keys = new Set(phrases.map(phraseKey));

  function add(list) {
    const fresh = [];
    for (const p of list) {
      const t = p.t.trim().replace(/\s+/g, ' ').slice(0, 120);
      if (!t) continue;
      const item = { t, m: p.m };
      const k = phraseKey(item);
      if (keys.has(k)) continue;
      keys.add(k); fresh.push(item);
    }
    if (!fresh.length) { toast('Already in the list', { icon: 'ℹ️' }); return 0; }
    onChange([...phrases, ...fresh].slice(0, 200));
    return fresh.length;
  }
  function addOne() {
    if (!text.trim()) return;
    if (add([{ t: text, m: mode }])) setText('');
    inputRef.current?.focus();
  }
  function addBulk() {
    const lines = bulkText.split(/\n|,(?![^"]*")/).map(s => s.trim()).filter(Boolean);
    const n = add(lines.map(t => ({ t, m: mode })));
    if (n) { toast.success(`Added ${n} phrase${n === 1 ? '' : 's'}`); setBulkText(''); setBulk(false); }
  }
  const remove = (p) => onChange(phrases.filter(x => phraseKey(x) !== phraseKey(p)));

  async function tryIt(useAi = false) {
    if (!tryText.trim()) return;
    setTrying(true);
    try {
      const res = await matchTestCommand(bId, tryText.trim(), { cId: cmdId ?? 0, cTrigger: trigger, cTriggerType: triggerType, cPhrases: phrases, cIsActive: isActive }, useAi);
      if (res.data?.success) setResult({ ...res.data, text: tryText.trim(), usedAi: useAi });
      else toast.error(res.data?.message ?? 'Couldn’t check');
    } catch (e) { toast.error(e?.response?.data?.message ?? 'Network error'); }
    setTrying(false);
  }

  const grouped = PHRASE_ORDER.map(m => ({ m, list: phrases.filter(p => p.m === m) })).filter(g => g.list.length);
  const m = result?.matched;
  const isMe = m && (Number(m.cId) === Number(cmdId ?? 0));
  const how = m?.how === 'trigger' ? 'its own trigger' : m?.how === 'catch-all' ? null
    : m?.how === 'ai' ? 'AI meaning' : m ? `${PHRASE_MODES[m.how]?.label ?? m.how} “${m.phrase}”` : null;
  const found = Object.entries(result?.found ?? {}).filter(([k]) => FOUND_LABELS[k] && k !== 'found.size_mb');

  return (
    <div className="cmx-phr">
      <div className="cmx-phr-main">
        <div className="cmx-phr-intro">
          <span className="cmx-phr-intro-ic"><i className="fa-regular fa-comments" /></span>
          <div>
            <b>Also reply when customers say…</b>
            <span>Customers don’t always type <code>{name}</code>. Add the words they actually use — the bot runs this command as if they’d typed it.</span>
          </div>
        </div>

        <div className="cmx-phr-add">
          <div className="cmx-phr-modes" role="radiogroup" aria-label="How to match">
            {PHRASE_ORDER.map(k => (
              <button key={k} type="button" role="radio" aria-checked={mode === k} className={mode === k ? 'is-active' : ''} onClick={() => setMode(k)} title={PHRASE_MODES[k].hint}>
                <i className={`fa-solid ${PHRASE_MODES[k].icon}`} /> {PHRASE_MODES[k].label}
              </button>
            ))}
          </div>
          <p className="cmx-phr-modehint"><b>{PHRASE_MODES[mode].hint}.</b> {PHRASE_MODES[mode].eg}{mode === 'ai' ? '. Uses the AI key from AI Integrations — without one, these are skipped.' : ''}</p>
          {bulk ? (
            <div className="cmx-phr-bulk">
              <textarea value={bulkText} onChange={e => setBulkText(e.target.value)} rows={5} autoFocus
                placeholder={'One per line, e.g.\ni need data\nsend me data\nbundle'} />
              <div className="cmx-phr-bulk-actions">
                <button type="button" className="cmx-btn ghost sm" onClick={() => setBulk(false)}>Cancel</button>
                <button type="button" className="cmx-btn primary sm" onClick={addBulk} disabled={!bulkText.trim()}>
                  <i className="fa-solid fa-plus" /> Add all as {PHRASE_MODES[mode].label}
                </button>
              </div>
            </div>
          ) : (
            <div className="cmx-phr-row">
              <input ref={inputRef} value={text} onChange={e => setText(e.target.value)} maxLength={120}
                placeholder={mode === 'ai' ? 'e.g. i want to buy internet data' : 'e.g. i need data'}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOne(); } }} />
              <button type="button" className="cmx-btn primary" onClick={addOne} disabled={!text.trim()}><i className="fa-solid fa-plus" /> Add</button>
              <button type="button" className="cmx-icon-btn" onClick={() => setBulk(true)} title="Paste many at once" aria-label="Paste many at once"><i className="fa-regular fa-paste" /></button>
            </div>
          )}
        </div>

        {grouped.length === 0 ? (
          <div className="cmx-phr-empty">
            <i className="fa-regular fa-lightbulb" />
            <span>No phrases yet — this command only answers <code>{name}</code>. A good start is one <b>Contains</b> word (like <code>data</code>), one <b>Similar</b> for typos, and an <b>AI meaning</b> sentence for everything else.</span>
          </div>
        ) : (
          <div className="cmx-phr-groups">
            {grouped.map(g => (
              <div key={g.m} className={`cmx-phr-group is-${g.m}`}>
                <div className="cmx-phr-group-head">
                  <i className={`fa-solid ${PHRASE_MODES[g.m].icon}`} /> {PHRASE_MODES[g.m].label}
                  <em>{g.list.length}</em>
                  <span>{PHRASE_MODES[g.m].hint}</span>
                </div>
                <div className="cmx-phr-chips">
                  {g.list.map(p => (
                    <span key={phraseKey(p)} className="cmx-phr-chip">
                      {p.t}
                      <button type="button" onClick={() => remove(p)} aria-label={`Remove ${p.t}`}><i className="fa-solid fa-xmark" /></button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
            <p className="cmx-phr-count">{phrases.length} phrase{phrases.length === 1 ? '' : 's'} · saved with the command, so they go live when you publish</p>
          </div>
        )}
      </div>

      <aside className="cmx-phr-side">
        <div className="cmx-phr-try">
          <b><i className="fa-solid fa-vial" /> Try a message</b>
          <span>Type what a customer might send. Uses the phrases above, even before you save.</span>
          <div className="cmx-phr-row">
            <input value={tryText} onChange={e => { setTryText(e.target.value); setResult(null); }} placeholder="abeg send me 2gb mtn"
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); tryIt(); } }} />
            <button type="button" className="cmx-btn ghost" onClick={() => tryIt()} disabled={trying || !tryText.trim()}>
              {trying ? <span className="cmx-spin" /> : 'Check'}
            </button>
          </div>
          {result && (
            <div className={`cmx-phr-result ${isMe ? 'is-me' : m && m.how !== 'catch-all' ? 'is-other' : result.ambiguous?.length ? 'is-other' : 'is-none'}`}>
              {result.ambiguous?.length ? (
                <><i className="fa-solid fa-code-branch" /><div><b>Customer is asked to choose</b><span>“Did you mean” {result.ambiguous.map(a => a.trigger).join(' · ')} — two commands match equally. Make one phrase more specific.</span></div></>
              ) : isMe ? (
                <><i className="fa-solid fa-circle-check" /><div><b>{name} answers</b><span>Matched by {how}.</span></div></>
              ) : m && m.how !== 'catch-all' ? (
                <><i className="fa-solid fa-arrow-right" /><div><b>{m.trigger} answers instead</b><span>Matched by {how}. Its own trigger or phrase comes first.</span></div></>
              ) : (
                <><i className="fa-solid fa-circle-minus" /><div><b>No command matched</b><span>{m ? 'Your catch-all answers.' : 'The bot stays silent.'}{result.usedAi ? ' The AI didn’t find a match either.' : ''}</span></div></>
              )}
            </div>
          )}
          {result && found.length > 0 && (
            <div className="cmx-phr-found">
              <span>Picked out for the command:</span>
              {found.map(([k, v]) => <code key={k} title={`{{${k}}}`}>{FOUND_LABELS[k]}: {v}</code>)}
            </div>
          )}
          {result?.aiPossible && (
            <button type="button" className="cmx-phr-ai" onClick={() => tryIt(true)} disabled={trying}>
              <i className="fa-solid fa-brain" /> Ask AI too <span>uses your AI key</span>
            </button>
          )}
        </div>

        <div className="cmx-phr-order">
          <b>How the bot decides</b>
          <ol>
            <li>Every command’s own trigger (like <code>/data</code>) — always checked first</li>
            <li><b>Exact</b> phrases</li>
            <li><b>Contains</b> phrases</li>
            <li><b>Similar</b> phrases</li>
            <li><b>AI meaning</b> — only if nothing else matched</li>
            <li>Your catch-all</li>
          </ol>
          <p>Only typed messages use phrases — button and list taps never do. If two commands match equally the customer gets <b>Did you mean…</b> buttons.</p>
          <p>Details in the message are passed to the command: <code>{'{{found.size}}'}</code> <code>{'{{found.network}}'}</code> <code>{'{{found.phone}}'}</code> <code>{'{{found.amount}}'}</code> <code>{'{{found.cable}}'}</code> <code>{'{{found.number}}'}</code>.</p>
        </div>
      </aside>
    </div>
  );
}

function CommandModal({ bId, cmd, defaultLanguage, presetCatchAll = false, onClose, onSaved }) {
  useBodyLock();
  const isEdit = !!cmd;

  function resolveEditorValue(command) {
    if (!command) return DEFAULT_CODE.json;
    if (command.cLanguage === 'json') {
      const raw = command.cActions;
      if (!raw) return '[]';
      if (typeof raw === 'string') {
        try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
      }
      return JSON.stringify(raw, null, 2);
    }
    return command.cCode ?? DEFAULT_CODE[command.cLanguage] ?? '';
  }

  const VALID_CMD_LANGUAGES = ['json', 'javascript', 'python'];
  const botDefaultLanguage = VALID_CMD_LANGUAGES.includes(defaultLanguage) ? defaultLanguage : 'json';

  const initial = useRef({
    cTrigger: cmd?.cTrigger ?? (presetCatchAll ? '*' : ''),
    cTriggerType: cmd?.cTriggerType ?? (presetCatchAll ? 'wildcard' : 'exact'),
    cType: cmd?.cType ?? (presetCatchAll ? 'wildcard' : 'command'),
    cLanguage: cmd?.cLanguage ?? botDefaultLanguage,
    cCode: cmd ? resolveEditorValue(cmd) : (DEFAULT_CODE[botDefaultLanguage] ?? DEFAULT_CODE.json),
    cIsActive: cmd?.cIsActive ?? 1,
    cPhrases: parsePhrases(cmd?.cPhrases),
  });
  const [form, setForm] = useState(initial.current);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [activeSection, setActiveSection] = useState('edit'); // 'edit' | 'phrases' | 'test'
  // On phones, start collapsed when editing so the code is visible straight away.
  const [showSettings, setShowSettings] = useState(() => !isEdit || typeof window === 'undefined' || window.innerWidth > 760);

  const dirty = JSON.stringify(form) !== JSON.stringify(initial.current);

  function set(k, v) {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === 'cLanguage' && !isEdit) next.cCode = DEFAULT_CODE[v] ?? '';
      // Keep cType, cTriggerType and cTrigger in sync for wildcard —
      // the DB matcher (matchLiveCommand) uses cTriggerType.
      if (k === 'cType' && v === 'wildcard') { next.cTriggerType = 'wildcard'; next.cTrigger = '*'; }
      if (k === 'cType' && v !== 'wildcard' && f.cTriggerType === 'wildcard') { next.cTriggerType = 'exact'; next.cTrigger = ''; }
      if (k === 'cTriggerType' && v === 'wildcard') { next.cType = 'wildcard'; next.cTrigger = '*'; }
      if (k === 'cTriggerType' && v !== 'wildcard' && f.cType === 'wildcard') { next.cType = 'command'; next.cTrigger = ''; }
      return next;
    });
  }

  function regexPreviewError() {
    if (form.cTriggerType !== 'regex' || !form.cTrigger) return null;
    try { new RegExp(form.cTrigger); return null; } catch (e) { return e.message; }
  }

  const jsonState = form.cLanguage === 'json' ? parseActions(form.cCode) : undefined;

  function addAction(key) {
    const arr = parseActions(form.cCode);
    if (arr === null) { toast.error('Fix the JSON first, then add an action'); return; }
    const snippet = JSON.parse(ACTION_SNIPPETS[key]);
    set('cCode', JSON.stringify([...arr, snippet], null, 2));
    toast.success(`Added ${key}`);
  }
  function formatJson() {
    const arr = parseActions(form.cCode);
    if (arr === null) { toast.error('Invalid JSON — can’t format yet'); return; }
    set('cCode', JSON.stringify(arr, null, 2));
  }

  function requestSave() {
    if (form.cTriggerType !== 'wildcard' && !form.cTrigger.trim()) { toast.error('Trigger is required'); return; }
    const regexErr = regexPreviewError();
    if (regexErr) { toast.error(`Invalid regex: ${regexErr}`); return; }
    setConfirming(true);
  }

  async function confirmSave() {
    setSaving(true);
    try {
      const normalizedForm = { ...form, cTrigger: form.cTriggerType === 'wildcard' ? '*' : form.cTrigger };
      const payload = normalizedForm.cLanguage === 'json'
        ? { bId, ...normalizedForm, cActions: normalizedForm.cCode, cCode: '' }
        : { bId, ...normalizedForm };
      const res = isEdit ? await updateCommand({ ...payload, cId: cmd.cId }) : await createCommand(payload);
      if (res.data?.success) {
        toast.success(isEdit
          ? (cmd.cStatus === 'live' ? 'Saved — not live yet. Publish when ready.' : 'Draft saved')
          : 'Command created as a draft');
        onSaved();
      } else {
        toast.error(res.data?.message ?? 'Failed to save command');
        setConfirming(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message ?? 'Network error — couldn’t save');
      setConfirming(false);
    }
    setSaving(false);
  }

  const tryClose = () => { if (saving) return; if (dirty) setDiscarding(true); else onClose(); };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); if (!confirming && !saving) requestSave(); }
      if (e.key === 'Escape' && !confirming && !discarding) tryClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const triggerTypeMeta = TRIGGER_TYPE_META[form.cTriggerType];
  const regexErr = regexPreviewError();
  const isCatchAll = form.cTriggerType === 'wildcard';

  return (
    <div className="cmx-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) tryClose(); }}>
      <div className="cmx-studio" role="dialog" aria-label={isEdit ? 'Edit command' : 'New command'}>
        {/* Top bar */}
        <div className="cmx-studio-bar">
          <button type="button" className="cmx-studio-close" onClick={tryClose} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
          <div className="cmx-studio-title">
            <span className="cmx-studio-kicker">{isEdit ? 'Edit command' : 'New command'}{dirty && <span className="cmx-dirty" title="Unsaved changes" />}</span>
            <b>{isCatchAll ? 'Anything else (catch-all)' : (form.cTrigger || 'Untitled trigger')}</b>
          </div>
          <div className="cmx-seg dark" role="tablist">
            <button type="button" role="tab" aria-selected={activeSection === 'edit'} className={activeSection === 'edit' ? 'is-active' : ''} onClick={() => setActiveSection('edit')}>
              <i className="fa-solid fa-pen-ruler" /> Build
            </button>
            {!isCatchAll && (
              <button type="button" role="tab" aria-selected={activeSection === 'phrases'} className={activeSection === 'phrases' ? 'is-active' : ''} onClick={() => setActiveSection('phrases')}>
                <i className="fa-regular fa-comments" /> Phrases{form.cPhrases.length > 0 && <em className="cmx-tab-count">{form.cPhrases.length}</em>}
              </button>
            )}
            <button type="button" role="tab" aria-selected={activeSection === 'test'} className={activeSection === 'test' ? 'is-active' : ''} onClick={() => setActiveSection('test')}>
              <i className="fa-solid fa-flask" /> Test
            </button>
          </div>
          <div className="cmx-studio-actions">
            <span className="cmx-kbd"><kbd>Ctrl</kbd><kbd>S</kbd></span>
            <button type="button" className="cmx-btn primary" onClick={requestSave} disabled={saving}>
              <i className="fa-solid fa-floppy-disk" /> <span>{isEdit ? 'Save draft' : 'Create'}</span>
            </button>
          </div>
        </div>

        {activeSection === 'edit' ? (
          <div className="cmx-studio-body">
            {/* Settings */}
            <aside className={`cmx-settings ${showSettings ? '' : 'is-collapsed'}`}>
              <button type="button" className="cmx-settings-toggle" onClick={() => setShowSettings(s => !s)} aria-expanded={showSettings}>
                <i className="fa-solid fa-sliders" /> Trigger & settings
                <i className={`fa-solid fa-chevron-${showSettings ? 'up' : 'down'}`} />
              </button>

              <div className="cmx-settings-body">
                <div className="cmx-field">
                  <label htmlFor="cmx-trigger">When a user sends</label>
                  <div className={`cmx-trigger-input ${regexErr ? 'is-bad' : ''}`}>
                    <i className="fa-regular fa-message" />
                    <input
                      id="cmx-trigger"
                      placeholder={form.cTriggerType === 'regex' ? 'order #(\\d+)' : '/start  or  Menu'}
                      value={isCatchAll ? '*' : form.cTrigger}
                      onChange={(e) => set('cTrigger', e.target.value)}
                      disabled={isCatchAll}
                      autoFocus={!isEdit}
                    />
                  </div>
                  {regexErr && <span className="cmx-field-err"><i className="fa-solid fa-circle-exclamation" /> Invalid regex: {regexErr}</span>}
                </div>

                <div className="cmx-field">
                  <label>Match</label>
                  <div className="cmx-match">
                    {TRIGGER_TYPES.map(t => (
                      <button key={t} type="button" className={form.cTriggerType === t ? 'is-active' : ''} onClick={() => set('cTriggerType', t)}>
                        <i className={`fa-solid ${TRIGGER_TYPE_META[t].icon}`} /> {TRIGGER_TYPE_META[t].label}
                      </button>
                    ))}
                  </div>
                  <span className="cmx-field-hint">{triggerTypeMeta.hint}</span>
                </div>

                {!isCatchAll && (
                  <div className="cmx-field">
                    <label>Also replies to</label>
                    <button type="button" className={`cmx-phr-sum ${form.cPhrases.length ? 'has' : ''}`} onClick={() => setActiveSection('phrases')}>
                      <i className="fa-regular fa-comments" />
                      <span>
                        {form.cPhrases.length
                          ? <><b>{form.cPhrases.length} phrase{form.cPhrases.length === 1 ? '' : 's'}</b> {form.cPhrases.slice(0, 3).map(p => `“${p.t}”`).join(', ')}{form.cPhrases.length > 3 ? '…' : ''}</>
                          : <><b>Add phrases</b> like “i need data”, so customers don’t have to type {form.cTrigger || 'the trigger'}</>}
                      </span>
                      <i className="fa-solid fa-chevron-right" />
                    </button>
                  </div>
                )}

                <div className="cmx-field">
                  <label>Type</label>
                  <div className="cmx-types">
                    {TYPES.map(t => (
                      <button key={t} type="button" className={form.cType === t ? 'is-active' : ''} onClick={() => set('cType', t)}>
                        <i className={`fa-solid ${TYPE_META[t].icon}`} /> {TYPE_META[t].label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="cmx-field">
                  <label>Language</label>
                  <div className="cmx-seg light full" role="radiogroup">
                    {LANGUAGES.map(l => (
                      <button key={l} type="button" role="radio" aria-checked={form.cLanguage === l} className={form.cLanguage === l ? 'is-active' : ''} onClick={() => set('cLanguage', l)}>
                        <span className="cmx-lang-dot" style={{ '--dot': LANG_META[l].dot }} /> {LANG_META[l].label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="cmx-field cmx-active-row">
                  <div>
                    <b>Active</b>
                    <span>{form.cIsActive ? 'Runs when live.' : 'Saved, but won’t run even if live.'}</span>
                  </div>
                  <Switch checked={!!form.cIsActive} onChange={(v) => set('cIsActive', v ? 1 : 0)} label={false} />
                </div>

                {isEdit && cmd?.cStatus === 'live' && (
                  <div className="cmx-note info">
                    <i className="fa-solid fa-shield-halved" />
                    <span>
                      {cmd.cHasDraft ? <>You’re editing <b>unpublished changes</b>. </> : null}
                      Customers keep getting the live version{cmd.cVersion ? ` (v${cmd.cVersion})` : ''} until you press <b>Publish changes</b>.
                      Message the bot from a linked phone to try your draft for real.
                    </span>
                  </div>
                )}
                {isEdit && cmd?.cStatus !== 'live' && (
                  <div className="cmx-note info">
                    <i className="fa-solid fa-flask" />
                    <span>Not live yet — only your linked phones can use it. Publish it from the list when it’s ready.</span>
                  </div>
                )}
              </div>
            </aside>

            {/* Code */}
            <section className="cmx-code">
              <div className="cmx-code-tools">
                <span className="cmx-code-title"><i className="fa-solid fa-bolt" /> Actions</span>
                {form.cLanguage === 'json' && (
                  <span className={`cmx-json-state ${jsonState === null ? 'is-bad' : 'is-ok'}`}>
                    {jsonState === null
                      ? <><i className="fa-solid fa-circle-xmark" /> Invalid JSON</>
                      : <><i className="fa-solid fa-circle-check" /> {jsonState.length} action{jsonState.length !== 1 ? 's' : ''}</>}
                  </span>
                )}
                <span className="cmx-editor-spacer" />
                {form.cLanguage === 'json' && (
                  <>
                    <button type="button" className="cmx-tool-btn" onClick={formatJson}><i className="fa-solid fa-align-left" /> Format</button>
                    <AddActionMenu onPick={addAction} />
                  </>
                )}
              </div>
              <CodeEditor value={form.cCode} onChange={(v) => set('cCode', v)} language={form.cLanguage} />
            </section>
          </div>
        ) : activeSection === 'phrases' && !isCatchAll ? (
          <div className="cmx-studio-phrases">
            <PhrasesPanel bId={bId} cmdId={cmd?.cId} trigger={form.cTrigger.trim()} triggerType={form.cTriggerType} isActive={form.cIsActive}
              phrases={form.cPhrases} onChange={(v) => set('cPhrases', v)} />
          </div>
        ) : (
          <div className="cmx-studio-test">
            <TestSandbox bId={bId} cmd={cmd} language={form.cLanguage} />
            {dirty && isEdit && <div className="cmx-note info"><i className="fa-solid fa-circle-info" /><span>Tests run the last <b>saved</b> version — including unpublished changes. Save your edits to test them.</span></div>}
          </div>
        )}
      </div>

      {confirming && (
        <ConfirmSheet
          icon="fa-floppy-disk"
          title={isEdit ? 'Save these changes?' : 'Create this command?'}
          body={isEdit
            ? (cmd.cStatus === 'live'
              ? <>Your changes are saved as <b>unpublished</b>. Customers keep the live version until you press <b>Publish changes</b> — try it from a linked phone first.</>
              : <>This saves your <b>draft</b>. It isn’t live yet.</>)
            : <>This creates <b>{isCatchAll ? 'a catch-all' : form.cTrigger}</b> as a draft {TYPE_META[form.cType].label.toLowerCase()}. Publish it afterwards to go live.</>}
          confirmLabel={isEdit ? 'Yes, save' : 'Yes, create'}
          busy={saving}
          onCancel={() => setConfirming(false)}
          onConfirm={confirmSave}
          cancelLabel="Keep editing"
        />
      )}
      {discarding && (
        <ConfirmSheet
          danger icon="fa-triangle-exclamation"
          title="Discard changes?"
          body="You have unsaved changes to this command."
          confirmLabel="Discard"
          onCancel={() => setDiscarding(false)}
          onConfirm={onClose}
          cancelLabel="Keep editing"
        />
      )}
    </div>
  );
}

function ConfirmSheet({ icon, title, body, confirmLabel, cancelLabel = 'Cancel', danger, busy, onCancel, onConfirm }) {
  return (
    <div className="cmx-overlay is-center is-top" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <div className="cmx-confirm" role="alertdialog" aria-label={title}>
        <span className={`cmx-confirm-ic ${danger ? 'is-danger' : ''}`}><i className={`fa-solid ${icon}`} /></span>
        <h3>{title}</h3>
        <p>{body}</p>
        <div className="cmx-confirm-actions">
          <button type="button" className="cmx-btn ghost" onClick={onCancel} disabled={busy}>{cancelLabel}</button>
          <button type="button" className={`cmx-btn ${danger ? 'danger' : 'primary'}`} onClick={onConfirm} disabled={busy}>
            {busy ? <><span className="cmx-spin" /> Working…</> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function QuickPreviewModal({ bId, cmd, onClose }) {
  useBodyLock();
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="cmx-overlay is-center" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cmx-sheet" role="dialog" aria-label="Preview command">
        <span className="cmx-grip" />
        <div className="cmx-sheet-head">
          <span className="cmx-sheet-ic"><i className="fa-regular fa-eye" /></span>
          <div>
            <h3>Preview “{cmd.cTrigger || '*'}”</h3>
            <p>What a user would actually see.</p>
          </div>
          <button type="button" className="cmx-icon-btn ghost" onClick={onClose} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
        </div>
        <div className="cmx-sheet-body">
          <TestSandbox bId={bId} cmd={cmd} language={cmd.cLanguage} />
        </div>
      </div>
    </div>
  );
}

// ── Version history ──────────────────────────────────────────
function agoShort(raw) {
  if (!raw) return '';
  const d = new Date(String(raw).replace(' ', 'T'));
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (Number.isNaN(s)) return '';
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
function fmtWhen(raw) {
  const d = new Date(String(raw).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

/** Readable text of a version for comparing: settings first, then the actions / code. */
function versionText(v) {
  if (!v) return '';
  const head = [
    `trigger: ${v.cTriggerType === 'wildcard' ? '* (catch-all)' : v.cTrigger ?? ''}`,
    `match: ${v.cTriggerType ?? 'exact'}`,
    `language: ${v.cLanguage ?? 'json'}`,
    `active: ${Number(v.cIsActive ?? 1) === 1 ? 'yes' : 'no'}`,
    ...parsePhrases(v.cPhrases).map(p => `phrase (${p.m}): ${p.t}`),
  ];
  let body = '';
  if ((v.cLanguage ?? 'json') === 'json') {
    const raw = v.cActions;
    try { body = JSON.stringify(typeof raw === 'string' ? JSON.parse(raw || '[]') : (raw ?? []), null, 2); } catch { body = String(raw ?? ''); }
  } else body = String(v.cCode ?? '');
  return [...head, '', ...body.split('\n')].join('\n');
}

/** Line diff (LCS). Returns [{ t: ' ' | '+' | '-', s }]. */
function diffLines(a, b) {
  const A = a.split('\n'), B = b.split('\n');
  if (A.length * B.length > 600000) return [...A.map(s => ({ t: '-', s })), ...B.map(s => ({ t: '+', s }))];
  const n = A.length, m = B.length;
  const L = Array.from({ length: n + 1 }, () => new Uint16Array(m + 1));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) L[i][j] = A[i] === B[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const out = []; let i = 0, j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { out.push({ t: ' ', s: A[i] }); i++; j++; }
    else if (L[i + 1][j] >= L[i][j + 1]) out.push({ t: '-', s: A[i++] });
    else out.push({ t: '+', s: B[j++] });
  }
  while (i < n) out.push({ t: '-', s: A[i++] });
  while (j < m) out.push({ t: '+', s: B[j++] });
  return out;
}

function DiffView({ from, to, fromLabel, toLabel }) {
  const rows = diffLines(versionText(from), versionText(to));
  const changed = rows.filter(r => r.t !== ' ').length;
  // Collapse long unchanged stretches
  const shown = [];
  rows.forEach((r, i) => {
    const near = rows.slice(Math.max(0, i - 2), i + 3).some(x => x.t !== ' ');
    if (r.t !== ' ' || near) shown.push({ ...r, i });
    else if (shown.length && shown[shown.length - 1].gap !== true) shown.push({ gap: true, i });
  });
  return (
    <div className="cmx-diff">
      <div className="cmx-diff-head">
        <span className="is-del"><i className="fa-solid fa-minus" /> {fromLabel}</span>
        <i className="fa-solid fa-arrow-right" />
        <span className="is-add"><i className="fa-solid fa-plus" /> {toLabel}</span>
        <em>{changed ? `${changed} line${changed === 1 ? '' : 's'} changed` : 'No differences'}</em>
      </div>
      <pre className="cmx-diff-body">
        {shown.map((r) => r.gap
          ? <span key={`g${r.i}`} className="cmx-diff-gap">⋯</span>
          : <span key={r.i} className={r.t === '+' ? 'is-add' : r.t === '-' ? 'is-del' : ''}><b>{r.t}</b>{r.s || ' '}</span>)}
      </pre>
    </div>
  );
}

function HistorySheet({ bId, cmd, onClose, onChanged }) {
  useBodyLock();
  const [versions, setVersions] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);   // vId being looked at
  const [against, setAgainst] = useState('live');    // 'live' | 'draft'
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState(null);      // { v, publish }

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !confirm) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, confirm]);

  useEffect(() => {
    let alive = true;
    listCommandVersions(bId, cmd.cId).then(res => {
      if (!alive) return;
      if (res.data?.success) {
        const vs = res.data.versions ?? [];
        setVersions(vs);
        setSelected(vs[1]?.vId ?? vs[0]?.vId ?? null);
      } else setError(res.data?.message ?? 'Could not load history');
    }).catch(() => alive && setError('Network error'));
    return () => { alive = false; };
  }, [bId, cmd.cId]);

  const isLive = cmd.cStatus === 'live';
  const liveV = isLive ? versions?.[0] : null;
  const sel = versions?.find(v => v.vId === selected) ?? null;
  const draftData = cmd.cHasDraft ? cmd : null; // editor view = draft on top
  const name = cmd.cTriggerType === 'wildcard' ? 'catch-all' : (cmd.cTrigger || '*');

  async function restore(v, publish) {
    setBusy(`${v.vId}:${publish ? 'p' : 'd'}`);
    try {
      const res = await restoreCommandVersion(bId, cmd.cId, v.vId, publish);
      if (res.data?.success) {
        toast.success(publish ? `v${v.vVersion} is live again` : `v${v.vVersion} restored as unpublished changes`);
        onChanged();
        onClose();
      } else toast.error(res.data?.message ?? 'Restore failed');
    } catch (e) { toast.error(e?.response?.data?.message ?? 'Network error'); }
    setBusy('');
    setConfirm(null);
  }

  const compareTo = against === 'draft' && draftData ? draftData : (liveV?.vData ?? (draftData ?? cmd));
  const compareLabel = against === 'draft' && draftData ? 'Unpublished changes' : liveV ? `Live now · v${liveV.vVersion}` : 'Current';

  return (
    <div className="cmx-overlay is-center" onMouseDown={(e) => { if (e.target === e.currentTarget && !confirm) onClose(); }}>
      <div className="cmx-sheet cmx-history" role="dialog" aria-label="Version history">
        <span className="cmx-grip" />
        <div className="cmx-sheet-head">
          <span className="cmx-sheet-ic"><i className="fa-solid fa-clock-rotate-left" /></span>
          <div>
            <h3>History · {name}</h3>
            <p>A version is saved every time you publish. Restore any of them — as a draft to test first, or straight to live.</p>
          </div>
          <button type="button" className="cmx-icon-btn ghost" onClick={onClose} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
        </div>
        <div className="cmx-sheet-body cmx-history-body">
          {error ? (
            <div className="cmx-note warn"><i className="fa-solid fa-circle-exclamation" /><span>{error}</span></div>
          ) : !versions ? (
            <div className="cmx-history-loading"><span className="cmx-spin dark" /> Loading history…</div>
          ) : versions.length === 0 ? (
            <div className="cmx-empty small">
              <span className="cmx-ic-lg"><i className="fa-solid fa-clock-rotate-left" /></span>
              <h3>No versions yet</h3>
              <p>{isLive ? 'This command went live before history existed. Its next publish saves the current version too, so you can always go back.' : 'Publish this command and every version from then on is kept here.'}</p>
            </div>
          ) : (
            <div className="cmx-history-grid">
              <ol className="cmx-timeline">
                {cmd.cHasDraft && (
                  <li className="is-draft">
                    <span className="cmx-tl-dot" />
                    <div><b>Unpublished changes</b><em>Saved {agoShort(cmd.cDraftAt)} · not live yet</em></div>
                  </li>
                )}
                {versions.map((v, i) => (
                  <li key={v.vId} className={`${selected === v.vId ? 'is-on' : ''} ${i === 0 && isLive ? 'is-live' : ''}`}>
                    <button type="button" onClick={() => setSelected(v.vId)}>
                      <span className="cmx-tl-dot" />
                      <div>
                        <b>v{v.vVersion}{i === 0 && isLive && <span className="cmx-status is-live sm"><span className="cmx-status-dot" />Live</span>}</b>
                        <em>{fmtWhen(v.vCreatedAt)}{v.vPublishedBy ? ` · ${v.vPublishedBy}` : ''}</em>
                        {v.vNote && <span className="cmx-tl-note">{v.vNote}</span>}
                      </div>
                    </button>
                  </li>
                ))}
              </ol>

              <div className="cmx-history-detail">
                {sel && (
                  <>
                    <div className="cmx-history-bar">
                      <b>v{sel.vVersion}</b>
                      <span>{fmtWhen(sel.vCreatedAt)}</span>
                      {draftData && liveV && (
                        <div className="cmx-seg light">
                          <button type="button" className={against === 'live' ? 'is-active' : ''} onClick={() => setAgainst('live')}>vs live</button>
                          <button type="button" className={against === 'draft' ? 'is-active' : ''} onClick={() => setAgainst('draft')}>vs unpublished</button>
                        </div>
                      )}
                    </div>
                    {liveV && sel.vId === liveV.vId && against === 'live'
                      ? <div className="cmx-note info"><i className="fa-solid fa-circle-info" /><span>This is the version customers get right now. Pick an older one to compare.</span></div>
                      : <DiffView from={sel.vData} to={compareTo} fromLabel={`v${sel.vVersion}`} toLabel={compareLabel} />}
                    {!(liveV && sel.vId === liveV.vId) && (
                      <div className="cmx-history-actions">
                        <button type="button" className="cmx-btn ghost" onClick={() => restore(sel, false)} disabled={!!busy}>
                          {busy === `${sel.vId}:d` ? <span className="cmx-spin dark" /> : <i className="fa-solid fa-pen-to-square" />} Restore as draft
                        </button>
                        <button type="button" className="cmx-btn primary" onClick={() => setConfirm({ v: sel })} disabled={!!busy}>
                          <i className="fa-solid fa-rotate-left" /> Restore &amp; publish
                        </button>
                      </div>
                    )}
                    <p className="cmx-history-hint">
                      <b>Restore as draft</b> puts v{sel.vVersion} in the editor as unpublished changes — customers keep the live version while you test it from a linked phone.
                      {' '}<b>Restore &amp; publish</b> makes it live straight away (the quick undo).
                    </p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {confirm && (
        <ConfirmSheet
          icon="fa-rotate-left"
          title={`Make v${confirm.v.vVersion} live?`}
          body={<>Customers get v{confirm.v.vVersion} of <b>{name}</b> from their next message. The current version stays in history, so you can undo this too.{cmd.cHasDraft && <><br /><br />Your unpublished changes will be replaced.</>}</>}
          confirmLabel="Restore & publish"
          busy={!!busy}
          onCancel={() => setConfirm(null)}
          onConfirm={() => restore(confirm.v, true)}
        />
      )}
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────
const FILTERS = [
  { key: 'all',   label: 'All' },
  { key: 'live',  label: 'Live' },
  { key: 'draft', label: 'Unpublished' },
  { key: 'off',   label: 'Off' },
];

export default function CommandsTab({ bId: bIdProp, bot }) {
  const bId = bIdProp ?? bot?.bId;
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'new' | cmd object
  const [previewTarget, setPreviewTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [publishingId, setPublishingId] = useState(null);
  const [togglingActiveId, setTogglingActiveId] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [discardTarget, setDiscardTarget] = useState(null);
  const [discardBusy, setDiscardBusy] = useState(false);

  async function load() {
    try {
      const res = await listCommands(bId);
      if (res.data?.success) setCommands(res.data.commands ?? []);
    } catch { toast.error('Failed to load commands'); }
    setLoading(false);
  }

  useEffect(() => { load(); }, [bId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteCommand(bId, deleteTarget.cId);
      toast.success('Command deleted');
      setCommands((prev) => prev.filter((c) => c.cId !== deleteTarget.cId));
      setDeleteTarget(null);
    } catch { toast.error('Failed to delete command'); }
    setDeleting(false);
  }

  async function handlePublish(cmd) {
    setPublishingId(cmd.cId);
    try {
      const res = await publishCommand(bId, cmd.cId);
      if (res.data?.success) {
        toast.success(cmd.cHasDraft ? `Changes to "${cmd.cTrigger || '*'}" are live` : `"${cmd.cTrigger || '*'}" is now live`);
        setCommands((prev) => prev.map((c) => c.cId === cmd.cId ? { ...c, cStatus: 'live', cHasDraft: false, cVersion: (c.cVersion || 0) + 1 } : c));
        load();
      } else {
        toast.error(res.data?.message ?? 'Failed to publish');
      }
    } catch { toast.error('Failed to publish command'); }
    setPublishingId(null);
  }

  async function handleUnpublish(cmd) {
    setPublishingId(cmd.cId);
    try {
      const res = await unpublishCommand(bId, cmd.cId);
      if (res.data?.success) {
        toast.success(`"${cmd.cTrigger || '*'}" moved back to draft`);
        setCommands((prev) => prev.map((c) => c.cId === cmd.cId ? { ...c, cStatus: 'draft', cHasDraft: false } : c));
      } else {
        toast.error(res.data?.message ?? 'Failed to unpublish');
      }
    } catch { toast.error('Failed to unpublish command'); }
    setPublishingId(null);
  }

  async function handleDiscard() {
    setDiscardBusy(true);
    try {
      const res = await discardCommandDraft(bId, discardTarget.cId);
      if (res.data?.success) { toast.success('Unpublished changes discarded'); setDiscardTarget(null); load(); }
      else toast.error(res.data?.message ?? 'Failed to discard');
    } catch { toast.error('Network error'); }
    setDiscardBusy(false);
  }

  async function handleToggleActive(cmd, active) {
    setTogglingActiveId(cmd.cId);
    try {
      const res = await toggleCommandActive(bId, cmd.cId, active);
      if (res.data?.success) {
        toast.success(`"${cmd.cTrigger || '*'}" ${active ? 'activated' : 'deactivated'}`);
        setCommands((prev) => prev.map((c) => c.cId === cmd.cId ? { ...c, cIsActive: active ? 1 : 0 } : c));
      } else {
        toast.error(res.data?.message ?? 'Failed to update');
      }
    } catch { toast.error('Failed to update command'); }
    setTogglingActiveId(null);
  }

  /* eslint-disable eqeqeq */
  const counts = {
    all: commands.length,
    live: commands.filter(c => c.cStatus === 'live').length,
    draft: commands.filter(c => c.cStatus !== 'live' || c.cHasDraft).length,
    off: commands.filter(c => c.cIsActive != 1).length,
  };
  const q = query.trim().toLowerCase();
  const filtered = commands.filter((c) => {
    if (filter === 'live' && c.cStatus !== 'live') return false;
    if (filter === 'draft' && c.cStatus === 'live' && !c.cHasDraft) return false;
    if (filter === 'off' && c.cIsActive == 1) return false;
    return !q || (c.cTrigger || '').toLowerCase().includes(q);
  });
  /* eslint-enable eqeqeq */
  const hasCatchAll = commands.some(c => c.cTriggerType === 'wildcard' || c.cType === 'wildcard');

  return (
    <div className="cmx">
      <header className="cmx-head">
        <div className="cmx-head-copy">
          <span className="cmx-eyebrow"><i className="fa-solid fa-terminal" /> Commands</span>
          <h2>How your bot listens and replies</h2>
          <p>Each command matches what a customer sends and runs a list of actions — send a message, show buttons, call an API, save data.</p>
          <ul className="cmx-head-points">
            <li><i className="fa-solid fa-shield-halved" /> Edits stay unpublished — customers keep the live version until you publish</li>
            <li><i className="fa-solid fa-flask" /> Your linked admin phones can try drafts on WhatsApp first</li>
            <li><i className="fa-solid fa-clock-rotate-left" /> Every publish is kept in History, so you can always go back</li>
          </ul>
        </div>
        <button type="button" className="cmx-btn primary lg" onClick={() => setModal('new')}><i className="fa-solid fa-plus" /> New command</button>
      </header>

      {loading ? (
        <div className="cmx-list">
          {[0, 1, 2].map(i => <div key={i} className="cmx-row cmx-skel"><span className="sk w30" /><span className="sk w70" /></div>)}
        </div>
      ) : commands.length === 0 ? (
        <div className="cmx-empty">
          <div className="cmx-empty-chat" aria-hidden="true">
            <span className="in">/start</span>
            <span className="out">👋 Welcome! What would you like to do?</span>
            <span className="btns"><em>Buy data</em><em>Check balance</em></span>
          </div>
          <h3>Create your first command</h3>
          <p>Start with <b>/start</b> or <b>Menu</b> — then add a catch-all so your bot always has a reply.</p>
          <button type="button" className="cmx-btn primary" onClick={() => setModal('new')}><i className="fa-solid fa-plus" /> New command</button>
        </div>
      ) : (
        <>
          <div className="cmx-toolbar">
            <div className="cmx-filters" role="tablist" aria-label="Filter commands">
              {FILTERS.map(f => (
                <button key={f.key} type="button" role="tab" aria-selected={filter === f.key}
                  className={filter === f.key ? 'is-active' : ''} onClick={() => setFilter(f.key)}>
                  {f.label} <em>{counts[f.key]}</em>
                </button>
              ))}
            </div>
            <div className="cmx-search">
              <i className="fa-solid fa-magnifying-glass" />
              <input placeholder="Search triggers…" value={query} onChange={(e) => setQuery(e.target.value)} />
              {query && <button type="button" onClick={() => setQuery('')} aria-label="Clear search"><i className="fa-solid fa-xmark" /></button>}
            </div>
          </div>

          {!hasCatchAll && (
            <button type="button" className="cmx-tip" onClick={() => setModal('new-catchall')}>
              <i className="fa-solid fa-lightbulb" />
              <span><b>No catch-all yet.</b> Customers who type something unexpected get no reply. Add a <em>Catch-all</em> command with a friendly menu.</span>
              <i className="fa-solid fa-arrow-right" />
            </button>
          )}

          {filtered.length === 0 ? (
            <div className="cmx-empty small">
              <span className="cmx-ic-lg"><i className="fa-solid fa-filter" /></span>
              <h3>No matches</h3>
              <p>{q ? <>Nothing matches “{query}”.</> : 'No commands in this view.'}</p>
              <button type="button" className="cmx-btn ghost sm" onClick={() => { setQuery(''); setFilter('all'); }}>Clear filters</button>
            </div>
          ) : (
            <div className="cmx-list">
              {filtered.map((cmd) => (
                <CommandRow
                  key={cmd.cId}
                  cmd={cmd}
                  onEdit={(c) => setModal(c)}
                  onPreview={(c) => setPreviewTarget(c)}
                  onDelete={(c) => setDeleteTarget(c)}
                  onPublish={handlePublish}
                  onUnpublish={handleUnpublish}
                  onDiscard={(c) => setDiscardTarget(c)}
                  onHistory={(c) => setHistoryTarget(c)}
                  onToggleActive={handleToggleActive}
                  publishing={publishingId === cmd.cId}
                  togglingActive={togglingActiveId === cmd.cId}
                />
              ))}
            </div>
          )}
        </>
      )}

      {modal && (
        <CommandModal
          bId={bId}
          cmd={typeof modal === 'string' ? null : modal}
          presetCatchAll={modal === 'new-catchall'}
          defaultLanguage={bot?.bLanguage}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}

      {previewTarget && <QuickPreviewModal bId={bId} cmd={previewTarget} onClose={() => setPreviewTarget(null)} />}

      {historyTarget && <HistorySheet bId={bId} cmd={historyTarget} onClose={() => setHistoryTarget(null)} onChanged={load} />}

      {discardTarget && (
        <ConfirmSheet
          danger icon="fa-eraser"
          title="Discard unpublished changes?"
          body={<>Your edits to <b>{discardTarget.cTrigger || '*'}</b> are thrown away. The live version{discardTarget.cVersion ? ` (v${discardTarget.cVersion})` : ''} stays exactly as it is.</>}
          confirmLabel="Discard changes"
          busy={discardBusy}
          onCancel={() => setDiscardTarget(null)}
          onConfirm={handleDiscard}
        />
      )}

      {deleteTarget && (
        <ConfirmSheet
          danger icon="fa-trash-can"
          title="Delete this command?"
          body={<><b>{deleteTarget.cTrigger || '*'}</b> will be removed and stop responding right away. This can’t be undone.</>}
          confirmLabel="Delete command"
          busy={deleting}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}